import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import agentService from '../../services/agentService';

const DEBOUNCE_MS = 3000;

const ScanTicket = () => {
  const location = useLocation();
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const lastScannedRef = useRef({ code: null, ts: 0 });

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(location.state?.event_id || '');
  const [operation, setOperation] = useState('checkin');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [manualQr, setManualQr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [eventMenuOpen, setEventMenuOpen] = useState(false);
  const eventMenuRef = useRef(null);

  // Close the event dropdown when clicking/tapping outside it
  useEffect(() => {
    const handler = (e) => {
      if (eventMenuRef.current && !eventMenuRef.current.contains(e.target)) {
        setEventMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  useEffect(() => {
    agentService.getAssignedEvents()
      .then((r) => {
        const evs = r.data?.data || [];
        setEvents(evs);
        if (!selectedEventId && evs.length === 1) {
          setSelectedEventId(String(evs[0].id));
        }
      })
      .catch(console.error)
      .finally(() => setLoadingEvents(false));
  }, [selectedEventId]);

  const doScan = useCallback(async (qrcode) => {
    if (!selectedEventId) {
      setResult({ success: false, message: 'Please select an event first.' });
      return;
    }
    const now = Date.now();
    if (qrcode === lastScannedRef.current.code && now - lastScannedRef.current.ts < DEBOUNCE_MS) return;
    lastScannedRef.current = { code: qrcode, ts: now };

    try {
      setSubmitting(true);
      const res = await agentService.scanTicket({
        qrcode,
        event_id: parseInt(selectedEventId, 10),
        operation,
      });
      const data = res.data;
      setResult({ success: data.success, message: data.message, user: data.user });
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.message || 'Scan failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }, [selectedEventId, operation]);

  // Always call the latest doScan without restarting the camera — changing
  // the operation (check-in/check-out) or event must NOT recreate the scanner
  const doScanRef = useRef(doScan);
  useEffect(() => { doScanRef.current = doScan; }, [doScan]);

  useEffect(() => {
    if (!scanning || !videoRef.current) return;
    const scanner = new QrScanner(
      videoRef.current,
      (r) => doScanRef.current(r.data),
      { returnDetailedScanResult: true, highlightScanRegion: true }
    );
    scannerRef.current = scanner;
    scanner.start().catch((e) => { console.error(e); setScanning(false); });
    return () => { scanner.stop(); scanner.destroy(); scannerRef.current = null; };
  }, [scanning]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualQr.trim()) { doScan(manualQr.trim()); setManualQr(''); }
  };

  const clearResult = () => setResult(null);

  return (
    <div className="max-w-lg mx-auto space-y-6 pb-10">

      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="bg-purple-100 rounded-xl p-2.5">
          <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Scan Tickets</h1>
          <p className="text-sm text-gray-600">Use camera or enter code manually</p>
        </div>
      </div>

      {/* Event selector */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-2">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Event</label>
        {loadingEvents ? (
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
        ) : (
          <div className="relative" ref={eventMenuRef}>
            <button
              type="button"
              onClick={() => setEventMenuOpen(o => !o)}
              className="w-full flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-left focus:outline-none focus:ring-2 focus:ring-purple-500 transition bg-white"
            >
              <span className={selectedEventId ? 'text-gray-900' : 'text-gray-500'}>
                {events.find(ev => String(ev.id) === String(selectedEventId))?.title || '— Select event —'}
              </span>
              <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${eventMenuOpen ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {eventMenuOpen && (
              <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 overflow-hidden">
                {events.length === 0 ? (
                  <p className="px-3 py-2.5 text-sm text-gray-500">No events assigned.</p>
                ) : events.map((ev) => {
                  const isSelected = String(ev.id) === String(selectedEventId);
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => { setSelectedEventId(String(ev.id)); setEventMenuOpen(false); clearResult(); }}
                      className={`w-full text-left px-3 py-2.5 text-sm transition flex items-center justify-between gap-2 ${
                        isSelected ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="truncate">{ev.title}</span>
                      {isSelected && (
                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Operation toggle */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 flex">
        <button
          onClick={() => { setOperation('checkin'); clearResult(); }}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition ${
            operation === 'checkin'
              ? 'bg-green-600 text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ✓ Check In
        </button>
        <button
          onClick={() => { setOperation('checkout'); clearResult(); }}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition ${
            operation === 'checkout'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ↩ Check Out
        </button>
      </div>

      {/* Camera scanner */}
      <div className="bg-black rounded-2xl overflow-hidden relative shadow-lg" style={{ minHeight: 280 }}>
        <video ref={videoRef} className="w-full" playsInline muted />
        {!scanning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 gap-3">
            <div className="border-2 border-white/20 rounded-2xl p-5">
              <svg className="w-10 h-10 text-white/40" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <button
              onClick={() => { setScanning(true); clearResult(); }}
              className="text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition hover:brightness-110"
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}
            >
              Start Camera
            </button>
          </div>
        )}
        {scanning && (
          <button
            onClick={() => setScanning(false)}
            className="absolute top-3 right-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-black/80 transition"
          >
            Stop
          </button>
        )}
      </div>

      {/* Manual entry */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Manual Entry</label>
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <input
            type="text"
            placeholder="Enter QR code (e.g. TKT-XXXXXXXXXXXX)"
            value={manualQr}
            onChange={(e) => setManualQr(e.target.value)}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
          />
          <button
            type="submit"
            disabled={submitting || !manualQr.trim()}
            className="disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-lg transition whitespace-nowrap hover:brightness-110"
            style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}
          >
            {submitting ? '…' : 'Submit'}
          </button>
        </form>
      </div>

      {/* Result banner */}
      {result && (
        <div
          className={`rounded-xl p-4 flex items-start gap-3 border ${
            result.success
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
            result.success ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {result.success ? (
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <p className={`font-semibold text-sm ${result.success ? 'text-green-800' : 'text-red-700'}`}>
              {result.message}
            </p>
            {result.success && result.user && (result.user.first_name || result.user.id > 0) && (
              <p className="text-xs text-gray-500 mt-0.5">
                {result.user.first_name} {result.user.last_name}
                {result.user.email && <> &mdash; {result.user.email}</>}
              </p>
            )}
          </div>
          <button onClick={clearResult} className="text-gray-300 hover:text-gray-500 text-xl leading-none transition">&times;</button>
        </div>
      )}
    </div>
  );
};

export default ScanTicket;
